import { Alert } from '@ctw/shared/components/alert';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { ZusAggregatedProfile } from '@ctw/shared/content/zus-aggregated-profile';
import { CTWProvider } from '@ctw/shared/context/ctw-context';
import { PatientProvider } from '@ctw/shared/context/patient-provider';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useZapContext } from '@ctw/shared/context/zap-context';
import { useTriggerHotloadPatient } from '@ctw/shared/hooks/use-trigger-hotload-patient';
import { tw } from '@ctw/shared/utils/tailwind';
import { useEffect } from 'react';
import { useZapConfigContext, ZapConfigProvider } from '../context/zap-config-provider';
import {
  sendOnAddToRecord,
  sendOnEhrWriteback,
  sendOnPatientSave,
  sendOnResourceSave,
} from '../utils/send-zap-message-event';

function ZapV2Content() {
  const { accessToken, zapConfig, requestTokenRefresh } = useZapConfigContext();
  const telemetry = useTelemetry();
  const { registerZapEventListener, unregisterZapEventListener } = useZapContext();

  // Fire hotload as early as we have a token + patient identifier, before
  // CTWProvider mounts. `PatientProviderProps` is `ThirdPartyID | PatientResourceID`,
  // both of which carry only the identifier fields the hook reads.
  useTriggerHotloadPatient({
    authToken: accessToken,
    ...zapConfig?.PatientProviderProps,
    fallbackEnv: zapConfig?.devProps?.fallbackEnv,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  useEffect(() => {
    telemetry.logger.info('Mounting zap in child frame');

    const onAddToRecord: Parameters<typeof registerZapEventListener>[1] = (event) => {
      void sendOnAddToRecord(
        telemetry.logger,
        zapConfig?.CTWProviderProps.ehr ?? 'unknown',
        event.payload.resource,
      );
    };

    registerZapEventListener('addToRecord', onAddToRecord);

    return () => {
      telemetry.logger.info('Unmounting zap from child frame');

      unregisterZapEventListener('addToRecord', onAddToRecord);
    };
  }, [telemetry, zapConfig]);

  if (!accessToken) {
    return <LoadingSpinner centered={true} message="Loading..." />;
  }

  if (!zapConfig) {
    return (
      <div className={tw`w-full`}>
        <Alert type="error" header="Missing Zus Aggregated Profile Configuration" />
      </div>
    );
  }

  return (
    <CTWProvider
      onAuthTokenExpiration={requestTokenRefresh}
      {...zapConfig.CTWProviderProps}
      builderId={zapConfig.CTWProviderProps.builderId}
      service="zap"
      serviceVersion={import.meta.env.VITE_GIT_SHA}
      serviceVariant="zap-v2"
      authToken={accessToken}
      devProps={zapConfig.devProps}
      onResourceSave={(resource, action, error) =>
        sendOnResourceSave(telemetry.logger, resource, action, error)
      }
      writebacks={{
        onWritebackSuccess: (_id, response) => {
          sendOnEhrWriteback(response);
        },
      }}
      ctwBehavioralFlags={{
        ...zapConfig.CTWProviderProps.ctwBehavioralFlags,
      }}
    >
      <PatientProvider
        {...zapConfig.PatientProviderProps}
        onPatientSave={(data) => sendOnPatientSave(telemetry.logger, data)}
      >
        <ZusAggregatedProfile />
      </PatientProvider>
    </CTWProvider>
  );
}

export function ZapV2() {
  return (
    <ZapConfigProvider>
      <ZapV2Content />
    </ZapConfigProvider>
  );
}
